'use client';

import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAtomValue, useSetAtom } from 'jotai';

import { ProjectResponseAudio, playOrEnqueueAudioAtom } from '@/shared-survey';
import {
  AudioWaveformPlayer,
  centralAudioElementAtom,
  currentPlayingIdAtom,
} from '@/shared-ui';

interface Props {
  audio: ProjectResponseAudio | undefined;
}

/**
 * Audio renderer with interactive waveform visualization.
 *
 * Uses the centralized Audio element from useAudioQueueController (via Jotai atoms) for:
 * - Synchronized waveform visualization (only for currently playing message)
 * - Automatic sequential playback
 * - Static waveform for non-playing messages
 *
 * The centralized controller handles all playback logic and queue management.
 */
export default function ProjectResponseMessageRendererAudio({ audio }: Props) {
  const centralAudio = useAtomValue(centralAudioElementAtom);
  const currentPlayingId = useAtomValue(currentPlayingIdAtom);

  const controller =
    !centralAudio || !audio?.messageId
      ? undefined
      : {
          audio: centralAudio,
          isActive: currentPlayingId === audio.messageId,
        };

  const playOrEnqueueAudio = useSetAtom(playOrEnqueueAudioAtom);

  const handlePlay = () => {
    if (!audio?.messageId) return;

    // If this audio is already the active one (just paused), resume it
    if (currentPlayingId === audio.messageId && centralAudio) {
      void centralAudio.play();
      return;
    }

    // Otherwise, queue it up to interrupt and play from start
    // We use playOrEnqueueAudio to handle cases where the queue might have been cleared
    playOrEnqueueAudio({
      messageId: audio.messageId,
      audioUrl: audio.audioUrl ?? null,
      status: 'ready',
      shouldInterrupt: true,
      // We don't have messageText here, but it's optional now
    });
  };

  // Show loading spinner while TTS is generating
  if (audio?.status === 'loading') {
    return (
      <FontAwesomeIcon
        icon={faSpinnerThird}
        spin
        className='size-4 self-center text-gray-400'
        data-test='audio-renderer-loading'
      />
    );
  }

  // Show interactive waveform player when audio is ready
  // AudioWaveformPlayer reads centralized audio from Jotai atoms
  if (audio?.status === 'success' && audio.audioUrl) {
    return (
      <AudioWaveformPlayer
        src={audio.audioUrl}
        barCount={50}
        className='self-center bg-transparent p-0'
        autoPlay={false}
        controller={controller}
        data-test='audio-waveform-player'
        onPlay={handlePlay}
      />
    );
  }

  return null;
}
